/**
 * Søknader: the action queue (owner, 2026-09-21; manager redesign, 2026-10-09; Fravær V1.4, #276).
 * Three indicators first: what waits for an answer (and the part that cannot be approved as it
 * stands, Må avklares), who is away the next seven days, and the planned shifts the open and
 * approved absences touch. Then the three lists, Krever handling, Kommende and Historikk, in one
 * five-column table: who, the period with the type and the amount, the balance, what to look at,
 * and one action, Vurder for a request the reader may decide, Se detaljer otherwise.
 *
 * Every balance and amount is the server's: the balance from GET /admin/absence-standing, the amount
 * from GET /absence-requests/deduction, read with the queries the drawer's approve button waits on
 * (queue.ts balanceOf). The list is sorted whole (queue.ts sortQueue), and the order is in the address.
 */
import { deductionQuery, standingQuery } from '@/api/absence';
import type { ApiAbsence, ApiAbsenceType, ApiShift, ApiUser } from '@/api/data';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { Drawer, Empty } from '@/ui';
import { fullName, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { useQueries } from '@tanstack/react-query';
import { addDays } from '@timefly/core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { daysOf } from './AbsenceDrawer';
import { describe, kindOfAbsence, rangeOf } from './AbsenceRange';
import { catInfo } from './category';
import { dayText, periodText, spanOf } from './dates';
import { type Filter, absenceMatches, teamsOf } from './filters';
import { FilterBar, Person, StatusChip } from './parts';
import {
  BUCKETS,
  type Bucket,
  type RowBalance,
  SORTS,
  type Sort,
  balanceOf,
  conflictsOf,
  needsClarification,
  peopleAway,
  queueOf,
  sortFor,
  sortQueue,
} from './queue';

const HEADS: Record<Bucket, string> = {
  handling: 'Krever handling',
  kommende: 'Kommende',
  historikk: 'Historikk',
};

/** The type as the catalogue has it, and whether it has been deleted since the request was sent. */
const typeOf = (a: ApiAbsence, types: ApiAbsenceType[]) => types.find((t) => t.id === a.absence_type?.id);
const typeGone = (a: ApiAbsence, types: ApiAbsenceType[]) =>
  typeOf(a, types)?.is_active === false || a.absence_type?.is_active === false;
const deductibleOf = (a: ApiAbsence, types: ApiAbsenceType[]) =>
  !!(typeOf(a, types)?.is_deductible ?? a.absence_type?.is_deductible);

/**
 * The Saldo column for every row on screen and every pending request (the indicators need those),
 * one standing read per person and year and one deduction read per pending request. The same query
 * keys as the drawer's, so opening a request reads nothing again.
 */
function useBalances(rows: ApiAbsence[], types: ApiAbsenceType[], today: string, maySee: boolean) {
  const thisYear = Number(today.slice(0, 4));
  const yearOf = (a: ApiAbsence) => {
    const { from, to } = spanOf(a);
    const y = Number(from.slice(0, 4));
    return from.slice(0, 4) === to.slice(0, 4) && (y === thisYear || y === thisYear + 1) ? y : null;
  };
  const wanted = rows.filter((a) => deductibleOf(a, types));
  const pairs = [
    ...new Set(wanted.map((a) => (yearOf(a) ? `${a.user_id}:${yearOf(a)}` : '')).filter(Boolean)),
  ].map((k) => k.split(':').map(Number) as [number, number]);
  const pending = wanted.filter((a) => a.status === 'pending');
  const standings = useQueries({ queries: pairs.map(([u, y]) => standingQuery(u, y, maySee)) });
  const charges = useQueries({
    queries: pending.map((a) =>
      deductionQuery({
        absence_type_id: a.absence_type?.id ?? null,
        start_date: a.start_date,
        end_date: a.end_date,
      }),
    ),
  });
  const out = new Map<number, RowBalance>();
  for (const a of rows) {
    const { from, to } = spanOf(a);
    const y = yearOf(a);
    const si = y ? pairs.findIndex(([u, yy]) => u === a.user_id && yy === y) : -1;
    const s = si >= 0 ? standings[si] : undefined;
    const ci = pending.indexOf(a);
    const c = ci >= 0 ? charges[ci] : undefined;
    out.set(
      a.id,
      balanceOf({
        pending: a.status === 'pending',
        deductible: deductibleOf(a, types),
        from,
        to,
        today,
        standing: !maySee
          ? { state: 'off' }
          : !s || s.isPending
            ? { state: 'loading' }
            : s.isError
              ? { state: 'error' }
              : { state: 'ok', value: s.data?.vacation?.left },
        charge:
          !c || c.isPending
            ? { state: 'loading' }
            : c.isError
              ? { state: 'error' }
              : { state: 'ok', value: c.data?.days },
      }),
    );
  }
  return out;
}

export function Soknader({
  all,
  users,
  types,
  shifts,
  filter,
  onFilter,
  filtersOpen,
  onFiltersOpen,
  teams,
  today,
  cancelIds,
  tenantId,
  loading,
  onOpen,
}: {
  all: ApiAbsence[];
  users: ApiUser[];
  types: ApiAbsenceType[];
  /** null while the shift list has not answered or failed: unknown, never «no shifts» (#262) */
  shifts: ApiShift[] | null;
  filter: Filter;
  onFilter: (f: Filter) => void;
  filtersOpen: boolean;
  onFiltersOpen: (open: boolean) => void;
  teams: string[];
  today: string;
  cancelIds: ReadonlySet<number>;
  tenantId: number | undefined;
  loading: boolean;
  onOpen: (a: ApiAbsence) => void;
}) {
  const can = useCan();
  const mayDecide = can('fravaer.godkjenne');
  const mayPay = can('ansatte.loenn');
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [affectedOpen, setAffectedOpen] = useState(false);
  const bucket = (BUCKETS.some(([k]) => k === sp.get('vis')) ? sp.get('vis') : 'handling') as Bucket;
  const sort = sortFor(bucket, sp.get('sorter'));
  const onlyClarify = bucket === 'handling' && sp.get('avklar') === '1';
  const setParams = (p: { vis?: Bucket; sorter?: Sort | null; avklar?: boolean }) =>
    setSp(
      (q) => {
        if (p.vis) {
          if (p.vis === 'handling') q.delete('vis');
          else q.set('vis', p.vis);
          // another list offers other orders: back to its default, which the select then shows
          if (p.vis !== bucket) q.delete('sorter');
          q.delete('avklar');
        }
        if (p.sorter !== undefined) {
          if (!p.sorter || p.sorter === SORTS[p.vis ?? bucket][0]?.[0]) q.delete('sorter');
          else q.set('sorter', p.sorter);
        }
        if (p.avklar !== undefined) {
          if (p.avklar) q.set('avklar', '1');
          else q.delete('avklar');
        }
        return q;
      },
      { replace: true },
    );
  const userOf = (id: number) => users.find((u) => u.id === id);
  const shown = all.filter((a) => absenceMatches(a, userOf(a.user_id), filter, tenantId));
  const lists = Object.fromEntries(BUCKETS.map(([k]) => [k, queueOf(shown, k, today, cancelIds)])) as Record<
    Bucket,
    ApiAbsence[]
  >;
  const balances = useBalances([...new Set([...lists.handling, ...lists[bucket]])], types, today, mayDecide);
  const clarify = lists.handling.filter((a) => needsClarification(a, typeGone(a, types), balances.get(a.id)));
  const clarifyIds = new Set(clarify.map((a) => a.id));
  const balancesPending = lists.handling.some((a) => balances.get(a.id)?.kind === 'loading');
  const picked = onlyClarify ? lists.handling.filter((a) => clarifyIds.has(a.id)) : lists[bucket];
  const rows = sortQueue(picked, sort, today, clarifyIds);
  const filtered = !!(filter.q || filter.team || filter.cat);

  // the next seven days, today included; approved absence only, whatever list is open
  const week = { from: today, to: addDays(today, 6) };
  const away = peopleAway(shown, week.from, week.to).size;
  // the planned shifts, from today on, inside an open request or an approved absence; one shift counted once
  const affected = shown
    .filter((a) => (a.status === 'pending' || a.status === 'accepted') && spanOf(a).to >= today)
    .map((a) => ({
      a,
      over: conflictsOf(a, kindOfAbsence(a, types), shifts ?? []).filter(
        (s) => (oslo(s.end_time)?.date ?? '') >= today,
      ),
    }))
    .filter((x) => x.over.length > 0);
  const affectedShifts = new Set(affected.flatMap((x) => x.over.map((s) => s.id))).size;
  const scope = [
    filter.team || T('Alle team'),
    filter.q ? T('Søkeresultat') : '',
    filter.cat ? T(catInfo(filter.cat).label) : '',
  ]
    .filter(Boolean)
    .join(', ');
  const n = (x: number) => (loading ? '–' : String(x));
  // the affected shifts are a count of a list that may not have come: a dash, not 0 (#262)
  const nShifts = (x: number) => (shifts ? n(x) : '–');

  return (
    <>
      <FilterBar
        value={filter}
        onChange={onFilter}
        teams={teams}
        // short enough for the phone's field (#272 B-T05: the long one was cut at 175 px); it still finds by
        // name, type and note
        placeholder={T('Søk i søknader')}
        open={filtersOpen}
        onOpen={onFiltersOpen}
      />
      <section className="fov" aria-label={T('Fravær i ditt utvalg')}>
        <div className={`fov-i needs${!loading && lists.handling.length === 0 ? ' zero' : ''}`}>
          <button
            type="button"
            className="fov-p"
            data-testid="ov-pending"
            onClick={() => setParams({ vis: 'handling' })}
          >
            <span className="fov-l">{T('Til behandling')}</span>
            <strong>
              {n(lists.handling.length)}
              <span> {lists.handling.length === 1 ? T('søknad') : T('søknader')}</span>
            </strong>
            <small>
              {T('Åpne køen')} <Icon name="chevronRight" />
            </small>
          </button>
          {loading || balancesPending ? (
            <small className="fov-n">{T('Henter saldo…')}</small>
          ) : clarify.length > 0 ? (
            <button
              type="button"
              className="fov-a"
              data-testid="ov-clarify"
              aria-pressed={onlyClarify}
              onClick={() => setParams({ vis: 'handling', avklar: true })}
            >
              {clarify.length === 1 ? T('1 må avklares') : T('{n} må avklares', { n: clarify.length })}
            </button>
          ) : (
            <small className="fov-n">{T('Ingen må avklares')}</small>
          )}
        </div>
        <div className={`fov-i ok${!loading && away === 0 ? ' zero' : ''}`}>
          <button
            type="button"
            className="fov-p"
            data-testid="ov-away"
            onClick={() => navigate('/fravaer/kalender?visning=neste7&status=innvilget')}
          >
            <span className="fov-l">{T('Fravær neste 7 dager')}</span>
            <strong>
              {n(away)}
              <span> {away === 1 ? T('ansatt') : T('ansatte')}</span>
            </strong>
            <small>
              {periodText(week.from, week.to)}, {T('innvilget')} <Icon name="chevronRight" />
            </small>
          </button>
        </div>
        <div className={`fov-i info${!loading && shifts && affectedShifts === 0 ? ' zero' : ''}`}>
          <button
            type="button"
            className="fov-p"
            data-testid="ov-shifts"
            disabled={!shifts}
            onClick={() => setAffectedOpen(true)}
          >
            <span className="fov-l">{T('Berørte vakter fremover')}</span>
            <strong>
              {nShifts(affectedShifts)}
              <span> {affectedShifts === 1 ? T('vakt') : T('vakter')}</span>
            </strong>
            <small>
              {!shifts
                ? T('Vaktplanen er ikke hentet')
                : affected.length === 1
                  ? T('1 fravær')
                  : T('{n} fravær', { n: affected.length })}{' '}
              <Icon name="chevronRight" />
            </small>
          </button>
          <small className="fov-n">{T('Dekning må vurderes')}</small>
        </div>
      </section>
      <fieldset className="mchips fchips" aria-label={T('Vis')}>
        {BUCKETS.map(([k, label]) => (
          <button key={k} type="button" aria-pressed={bucket === k} onClick={() => setParams({ vis: k })}>
            {T(label)}
            {/* no count until the list has answered (#272 B-23) */}
            <span className="mtc">{loading ? '–' : lists[k].length}</span>
          </button>
        ))}
      </fieldset>
      {onlyClarify && (
        <div className="fscope" data-testid="clarify-scope">
          <span>
            {T('Må avklares: slettet fraværstype, ukjent saldo eller saldo under 0 etter innvilgelse')}
          </span>
          <button type="button" className="btn quiet" onClick={() => setParams({ avklar: false })}>
            {T('Vis hele køen')}
          </button>
        </div>
      )}
      <section className="mcard">
        <div className="fqh">
          <div>
            <h2>
              {bucket === 'handling'
                ? onlyClarify
                  ? rows.length === 1
                    ? T('1 søknad må avklares')
                    : T('{n} søknader må avklares', { n: loading ? '–' : rows.length })
                  : rows.length === 1
                    ? T('1 søknad å behandle')
                    : T('{n} søknader å behandle', { n: loading ? '–' : rows.length })
                : T(HEADS[bucket])}
            </h2>
            <small>{scope}</small>
          </div>
          <label className="fsort">
            {T('Sorter etter')}
            <select
              className="inp"
              value={sort}
              data-testid="queue-sort"
              onChange={(e) => setParams({ sorter: e.target.value as Sort })}
            >
              {SORTS[bucket].map(([k, label]) => (
                <option key={k} value={k}>
                  {T(label)}
                </option>
              ))}
            </select>
          </label>
        </div>
        {loading ? (
          <Empty>{T('Henter søknader…')}</Empty>
        ) : rows.length ? (
          <>
            <table
              className="fq4"
              aria-label={
                bucket === 'handling'
                  ? T('Søknader til behandling')
                  : bucket === 'kommende'
                    ? T('Kommende fravær')
                    : T('Fraværshistorikk')
              }
            >
              <thead>
                <tr>
                  <th scope="col">{T('Ansatt')}</th>
                  <th scope="col">{T('Fravær og periode')}</th>
                  <th scope="col">
                    {bucket === 'handling' ? T('Saldo ved innvilgelse') : T('Tilgjengelig saldo')}
                  </th>
                  <th scope="col">{bucket === 'handling' ? T('Til vurdering') : T('Vaktpåvirkning')}</th>
                  <th scope="col">
                    <span className="sr">{T('Handling')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <QueueRow
                    key={a.id}
                    a={a}
                    u={userOf(a.user_id)}
                    types={types}
                    shifts={shifts}
                    bucket={bucket}
                    sort={sort}
                    today={today}
                    balance={balances.get(a.id)}
                    cancel={cancelIds.has(a.id)}
                    mayDecide={mayDecide}
                    mayPay={mayPay}
                    tenantId={tenantId}
                    onOpen={onOpen}
                  />
                ))}
              </tbody>
            </table>
            <p className="fqf">
              {bucket === 'handling'
                ? rows.length === 1
                  ? T('Viser 1 søknad')
                  : T('Viser {n} søknader', { n: rows.length })
                : T('Viser {n} fravær', { n: rows.length })}
            </p>
          </>
        ) : (
          <Empty>
            {onlyClarify
              ? T('Ingen søknader må avklares.')
              : filtered
                ? T('Ingen søknader passer filteret.')
                : bucket === 'handling'
                  ? T('Ingen søknader venter. Alt er besvart.')
                  : bucket === 'kommende'
                    ? T('Ikke noe innvilget fravær fremover.')
                    : T('Ingen tidligere fravær.')}
          </Empty>
        )}
        <p className="fnote">
          {bucket === 'handling'
            ? T(
                'Saldo ved innvilgelse viser virkningen av den enkelte søknaden. Andre ventende søknader er ikke trukket fra.',
              )
            : T('Tilgjengelig saldo er dagens saldo for ferieåret, ikke saldoen på fraværstidspunktet.')}
        </p>
      </section>
      <Drawer
        open={affectedOpen}
        onClose={() => setAffectedOpen(false)}
        title={T('Berørte vakter')}
        sub={scope}
        footer={
          <>
            <span className="sp" />
            <button type="button" className="btn" onClick={() => setAffectedOpen(false)}>
              {T('Lukk')}
            </button>
          </>
        }
      >
        <p className="hint">
          {T(
            'Publiserte vakter fra i dag som overlapper innvilget fravær eller ventende søknader. Det er ikke et mål på udekkede vakter.',
          )}
        </p>
        {affected.length === 0 ? (
          <Empty>{T('Ingen berørte vakter fremover i dette utvalget.')}</Empty>
        ) : (
          affected.map(({ a, over }) => {
            const name = fullName(a.user ?? userOf(a.user_id));
            return (
              <section key={a.id} className="faff" data-testid="affected-record">
                <div className="faff-h">
                  <b>{name}</b>
                  <StatusChip status={a.status} cancel={cancelIds.has(a.id)} />
                </div>
                <small>
                  {periodText(spanOf(a).from, spanOf(a).to)}, {a.absence_type?.name ?? T('Fravær')}
                </small>
                <ul>
                  {over.map((s) => (
                    <li key={s.id}>
                      <span>{dayText(oslo(s.start_time)?.date ?? '')}</span>
                      <span>
                        {oslo(s.start_time)?.time}–{oslo(s.end_time)?.time}
                      </span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="btn quiet"
                  aria-label={T('Se fravær for {name}', { name })}
                  // the record opens over this sheet, which stays: closing the record comes back to this
                  // button and the list, not to the page heading (FV-05)
                  onClick={() => onOpen(a)}
                >
                  {T('Se fravær')} <Icon name="chevronRight" />
                </button>
              </section>
            );
          })
        )}
        <p className="hint">{T('Godkjenning flytter ikke vaktene. Følg opp bemanningen i vaktplanen.')}</p>
      </Drawer>
    </>
  );
}

function BalanceCell({ b }: { b: RowBalance | undefined }) {
  if (!b || b.kind === 'loading') return <span className="q-mut">{T('Henter…')}</span>;
  if (b.kind === 'none') return <span className="q-mut">{T('Ingen ferietrekk')}</span>;
  if (b.kind === 'hidden') return <span className="q-mut">{T('Ikke tilgjengelig')}</span>;
  if (b.kind === 'split') return <span className="q-mut">{T('Over to ferieår')}</span>;
  if (b.kind === 'older') return <span className="q-mut">{T('Tidligere ferieår')}</span>;
  if (b.kind === 'unknown') return <strong className="q-unk">{T('Ukjent')}</strong>;
  if (b.kind === 'now')
    return (
      <span className="q-flow">
        <strong className={b.now < 0 ? 'q-neg' : ''}>{num(b.now, 1)}</strong>
        <span className="q-unit">{b.now === 1 ? T('dag') : T('dager')}</span>
      </span>
    );
  if (b.kind !== 'after') return null;
  return (
    <span className="q-flow">
      <span className="sr">{T('Nå')} </span>
      <span className="q-before">{num(b.now, 1)}</span>
      <Icon name="arrowRight" />
      <span className="sr">{T('Ved innvilgelse')} </span>
      <strong className={b.after < 0 ? 'q-neg' : ''}>{num(b.after, 1)}</strong>
      <span className="q-unit">{b.after === 1 ? T('dag') : T('dager')}</span>
    </span>
  );
}

function QueueRow({
  a,
  u,
  types,
  shifts,
  bucket,
  sort,
  today,
  balance,
  cancel,
  mayDecide,
  mayPay,
  tenantId,
  onOpen,
}: {
  a: ApiAbsence;
  u: ApiUser | undefined;
  types: ApiAbsenceType[];
  shifts: ApiShift[] | null;
  bucket: Bucket;
  sort: Sort;
  today: string;
  balance: RowBalance | undefined;
  cancel: boolean;
  mayDecide: boolean;
  mayPay: boolean;
  tenantId: number | undefined;
  onOpen: (a: ApiAbsence) => void;
}) {
  const kind = kindOfAbsence(a, types);
  const type = typeOf(a, types);
  const { from, to } = spanOf(a);
  const over = shifts ? conflictsOf(a, kind, shifts) : null;
  const pending = a.status === 'pending';
  const name = fullName(a.user ?? u);
  // Fravær's people are the active ones: a request on somebody not among them is a former employee's
  const former = !u && !!a.user;
  // the name the request carries is the type as it was booked; the catalogue's is the fallback
  const typeName = a.absence_type?.name ?? type?.name ?? T('Fravær');
  const gone = typeGone(a, types);
  const n = kind === 'date' ? daysOf(from, to) : null;
  // a part of a day, or across midnight: the hours between the two instants
  const hours = kind === 'date' ? null : (Date.parse(a.end_date) - Date.parse(a.start_date)) / 36e5;
  const period = kind === 'date' ? periodText(from, to) : describe(rangeOf(a), kind);
  const amount =
    n != null
      ? `${n} ${n === 1 ? T('dag') : T('dager')}`
      : hours != null && hours > 0
        ? `${num(hours, 1)} ${T('timer')}`
        : T('timer');
  const graded = a.sick_grade != null ? `, ${a.sick_grade} %` : '';
  const decide = mayDecide && (pending || cancel);
  const action = decide ? T('Vurder') : T('Se detaljer');
  const issues = [
    cancel ? T('Ønsker avlysning') : '',
    pending && gone ? T('Utgått fraværstype') : '',
    pending && balance?.kind === 'unknown' ? T('Saldo ukjent') : '',
    pending && balance?.kind === 'after' && balance.after < 0 ? T('Under saldo') : '',
    // the pay export is the administrator's screen, so its gap is their warning too
    pending && mayPay && type && !type.salary_type_code && !type.a_melding ? T('Lønnsart mangler') : '',
  ].filter(Boolean);
  const received = oslo(a.created_at)?.date;
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the row click is a pointer shortcut; the keyboard path is the row's button
    <tr
      className="row"
      data-testid={pending ? 'pending-row' : cancel ? 'cancel-row' : 'absence-row'}
      onClick={() => onOpen(a)}
    >
      <td className="q-who">
        <Person
          id={a.user_id}
          name={name}
          sub={
            [
              teamsOf(u, tenantId)
                .map((t) => t.name)
                .join(', '),
              former ? T('Tidligere ansatt') : '',
            ]
              .filter(Boolean)
              .join(', ') || undefined
          }
        />
      </td>
      <td className="q-per">
        <span className="q-lbl">{T('Fravær og periode')}</span>
        <strong>{period}</strong>
        <span className="q-desc">
          {typeName}, {amount}
          {graded}
          {gone ? `, ${T('Utgått type')}` : ''}
        </span>
        {pending && to < today && <small className="q-past">{T('Fraværet har vært')}</small>}
        {sort === 'eldste' && received && (
          <small className="q-past">
            {T('Mottatt {date}', { date: periodText(received, received, false) })}
          </small>
        )}
        {!pending && <StatusChip status={a.status} cancel={cancel} />}
      </td>
      <td className="q-bal">
        <span className="q-lbl">
          {bucket === 'handling' ? T('Saldo ved innvilgelse') : T('Tilgjengelig saldo')}
        </span>
        <BalanceCell b={balance} />
      </td>
      <td className="q-rev">
        <span className="q-lbl">{bucket === 'handling' ? T('Til vurdering') : T('Vaktpåvirkning')}</span>
        {issues.map((x) => (
          <span key={x} className="q-issue">
            <Icon name="warn" />
            <span>{x}</span>
          </span>
        ))}
        {over == null ? (
          // a list that has not answered is not a check that found nothing (#262)
          <span className="q-mut">{T('Vakter ikke hentet')}</span>
        ) : over.length > 0 ? (
          <span className="q-imp">
            <Icon name="calendar" />
            <span>{over.length === 1 ? T('1 vakt berørt') : T('{n} vakter berørt', { n: over.length })}</span>
          </span>
        ) : (
          issues.length === 0 && <span className="q-mut">{T('Ingen vakter berørt')}</span>
        )}
      </td>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stops the row click; the button inside is the keyboard path */}
      <td className="q-act" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="mlink"
          aria-label={`${action} ${name}, ${period}`}
          onClick={() => onOpen(a)}
        >
          {action} <Icon name="chevronRight" />
        </button>
      </td>
    </tr>
  );
}
